import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NavLinks = ({
  location,
  isAuthenticated,
  userRole,
  isMobile,
  closeMenu,
}) => (
  <>
    <motion.div
      initial={isMobile ? { x: -20, opacity: 0 } : false}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <Link
        to="/about"
        className={`hover:text-primary ${
          location.pathname === "/about" ? "text-primary-dark" : ""
        } ${isMobile ? "block py-2" : ""}`}
        onClick={closeMenu}
      >
        About
      </Link>
    </motion.div>
    <motion.div
      initial={isMobile ? { x: -20, opacity: 0 } : false}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <Link
        to="/research"
        className={`hover:text-primary ${
          location.pathname === "/research" ? "text-primary-dark" : ""
        } ${isMobile ? "block py-2" : ""}`}
        onClick={closeMenu}
      >
        Research
      </Link>
    </motion.div>
    <motion.div
      initial={isMobile ? { x: -20, opacity: 0 } : false}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.2, delay: 0.1 }}
    >
      <Link
        to="/service"
        className={`hover:text-primary ${
          location.pathname === "/service" ? "text-primary-dark" : ""
        } ${isMobile ? "block py-2" : ""}`}
        onClick={closeMenu}
      >
        Service
      </Link>
    </motion.div>
    <motion.div
      initial={isMobile ? { x: -20, opacity: 0 } : false}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.2, delay: 0.2 }}
    >
      {/* <Link
        to="/product"
        className={`hover:text-primary ${
          location.pathname === "/product" ? "text-primary-dark" : ""
        } ${isMobile ? "block py-2" : ""}`}
        onClick={closeMenu}
      >
        Product
      </Link> */}
      <Link
        to="/product"
        className={`hover:text-primary ${
          location.pathname.startsWith("/product") ? "text-primary-dark" : ""
        } ${isMobile ? "block py-2" : ""}`}
        onClick={closeMenu}
      >
        Product
      </Link>
    </motion.div>
    <motion.div
      initial={isMobile ? { x: -20, opacity: 0 } : false}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.2, delay: 0.3 }}
    >
      <Link
        to="/blog"
        className={`hover:text-primary ${
          location.pathname === "/blog" ? "text-primary-dark" : ""
        } ${isMobile ? "block py-2" : ""}`}
        onClick={closeMenu}
      >
        Blog
      </Link>
    </motion.div>
    <motion.div
      initial={isMobile ? { x: -20, opacity: 0 } : false}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.2, delay: 0.4 }}
    >
      <Link
        to="/contact"
        className="bg-primary-dark px-4 py-2 rounded hover:bg-primary text-center block"
        onClick={closeMenu}
      >
        Contact Us
      </Link>
    </motion.div>
    {isAuthenticated && (
      <motion.div
        initial={isMobile ? { x: -20, opacity: 0 } : false}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.2, delay: 0.5 }}
      >
        <Link
          to={
            ["admin", "admin-proyek", "admin-produk", "supervisor"].includes(
              userRole
            )
              ? "/admin/dashboard"
              : "/monitoring/overview"
          }
          className="bg-primary-dark px-4 py-2 rounded hover:bg-primary text-center block"
          onClick={(e) => {
            const expiresAt = localStorage.getItem("expiresAt");
            if (expiresAt && Date.now() > parseInt(expiresAt)) {
              e.preventDefault();
              localStorage.clear();
              window.location.href = "/login";
            } else {
              closeMenu();
            }
          }}
        >
          {["admin", "admin-proyek", "admin-produk"].includes(userRole)
            ? "Dashboard"
            : userRole === "supervisor"
            ? "Monitoring"
            : null}
        </Link>
      </motion.div>
    )}
  </>
);

const Navbar = ({ isVisible }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = localStorage.getItem("token");
  const [userRole, setUserRole] = useState(null);
  const [keys, setKeys] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Get user role from localStorage
  useEffect(() => {
    if (isAuthenticated) {
      try {
        const userData = JSON.parse(localStorage.getItem("user"));
        if (userData && userData.role) {
          setUserRole(userData.role);
        }
      } catch (err) {
        console.error("Error parsing user data:", err);
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      setKeys((prev) => [...prev, e.key]);
      if (e.altKey && keys.includes("a") && keys.includes("d")) {
        navigate("/admin");
        setKeys([]);
      }
      const timer = setTimeout(() => setKeys([]), 1000);
      return () => clearTimeout(timer);
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [navigate, keys]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <motion.nav
      initial={false}
      animate={{
        y: isVisible ? 0 : "-100%",
      }}
      transition={{ duration: 0.3 }}
      className="bg-deep p-4 fixed w-full z-30"
    >
      <div className="max-w-7xl mx-auto flex justify-between items-center px-4">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Link to="/" onClick={closeMenu}>
            <img src="/images/logo.svg" alt="ADHIM" className="h-12 md:h-16" />
          </Link>
        </motion.div>

        <motion.button
          className="md:hidden text-white"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileTap={{ scale: 0.95 }}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </motion.button>

        <div className="hidden md:flex items-center gap-6 text-white">
          <NavLinks
            location={location}
            isAuthenticated={isAuthenticated}
            userRole={userRole}
            closeMenu={closeMenu}
          />
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute top-full left-0 right-0 bg-[#012541] p-4 md:hidden overflow-hidden"
            >
              <div className="flex flex-col gap-4 text-white">
                <NavLinks
                  location={location}
                  isAuthenticated={isAuthenticated}
                  userRole={userRole}
                  isMobile
                  closeMenu={closeMenu}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
